import { useEffect, useState } from 'react'
import { Download, X } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

const DISMISS_KEY = 'debtos:installPrompt:dismissedAt'
const DISMISS_COOLDOWN_DAYS = 7

/**
 * Lightweight install prompt for installable PWAs.
 *
 * Shown once Chrome / Edge / Samsung Internet on Android fires
 * `beforeinstallprompt`. We capture the event, suppress the browser's own
 * mini-bar (handled implicitly by preventDefault), and display our own
 * dismissable card. Once dismissed, we hold off for a week before re-asking.
 *
 * Safari iOS does not fire this event; users have to use Share → Add to
 * Home Screen, which is fine — we just won't render anything for them.
 */
export function InstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      const dismissedAt = localStorage.getItem(DISMISS_KEY)
      if (dismissedAt) {
        const ageMs = Date.now() - new Date(dismissedAt).getTime()
        const cooldownMs = DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000
        if (ageMs < cooldownMs) return
      }
      setEvent(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  // Once installed, the browser fires `appinstalled` and we hide the prompt
  useEffect(() => {
    const handler = () => setHidden(true)
    window.addEventListener('appinstalled', handler)
    return () => window.removeEventListener('appinstalled', handler)
  }, [])

  if (!event || hidden) return null

  const handleInstall = async () => {
    void event.prompt()
    const choice = await event.userChoice
    if (choice.outcome === 'dismissed') {
      localStorage.setItem(DISMISS_KEY, new Date().toISOString())
    }
    setEvent(null)
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString())
    setEvent(null)
  }

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-2rem)]">
      <div className="flex items-center gap-3 bg-slate-900 border border-emerald-700/60 rounded-xl px-4 py-3 shadow-2xl">
        <div className="w-9 h-9 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center shrink-0">
          <Download className="w-4 h-4 text-emerald-400" />
        </div>
        <div className="text-sm text-slate-200 min-w-0">
          <div className="font-medium">Install DebtOS</div>
          <div className="text-xs text-slate-400 mt-0.5">
            Add to your home screen for offline access.
          </div>
        </div>
        <button
          onClick={handleInstall}
          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold transition-colors shrink-0"
        >
          Install
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-slate-400 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
